import { AddIcon, Button } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import React, { FunctionComponent, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import { PlanType, PrescriptionDrug } from "../../../@types";
import routes from "../../../app/routes";
import { AppContext } from "../../../app/store";
import { getPDEInfo, useTranslate } from "../../../helpers";
import { DrugCard } from "../components";
import { AlertMessage, InsulinDrugCoachmark } from "../../../components";
import { alertMessageHierarchy } from "../../../app/contexts/AlertMessaging/constants";
import { useAlertMessaging } from "../../../app/contexts";
import { getMessageToShow } from "../../../app/contexts/AlertMessaging/helpers";
import { AlertMessageHierarchyKeys } from "../../../app/contexts/AlertMessaging/types";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";

interface DrugCardsContainerProps {
  drugs: PrescriptionDrug[];
  onClickAddPDEs: () => void;
  planType?: PlanType;
}

const DrugCardsContainer: FunctionComponent<DrugCardsContainerProps> = ({
  drugs,
  onClickAddPDEs,
  planType,
}) => {
  const t = useTranslate();
  const alphabetizedDrugs = drugs.sort((a, b) => a.name.localeCompare(b.name));
  const drugsToDisplay = alphabetizedDrugs.slice(0, 6);
  const url = window.location.href.split("/summary?")[0];
  const location = useLocation();
  const totalDrugCount = drugs.length;
  const editButtonText =
    totalDrugCount > 6
      ? t("summary_page.drugs.actions.edit_drugs.more_than_6")
      : t("summary_page.drugs.actions.edit_drugs");
  const {
    state: { beneficiary },
    dispatch,
  } = useContext(AppContext);
  const showAddPDEButton = beneficiary
    ? getPDEInfo(drugs, beneficiary.pdes).unsavedPdes.length > 0
    : false;

  const onClickAddModifyDrugs = () => {
    const drugsLinkText =
      totalDrugCount > 0
        ? editButtonText
        : t("summary_page.drugs.actions.add_drugs");
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.SECONDARY,
          buttonType: AnalyticsButtonType.BUTTON,
          text: drugsLinkText,
        },
        linkUrl: `${url}${routes.summary.managePrescriptions}${location.search}`,
      },
    });
  };
  const {
    state: { messages },
  } = useAlertMessaging();

  const messageToShow = getMessageToShow(
    messages,
    alertMessageHierarchy[AlertMessageHierarchyKeys.SUMMARY_PAGE__DRUG_SECTION],
    t,
    {
      routerState: {
        nextRoute: {
          ...location,
        },
      },
      planType,
    }
  );
  return (
    <div className="mct-c-summary-page-drug-cards-container">
      <InsulinDrugCoachmark />
      <p
        className={classNames(
          "ds-u-measure--base",
          "ds-u-font-size--lg",
          "ds-u-margin-top--0",
          { "mct-hide-print": totalDrugCount > 0 }
        )}
      >
        {totalDrugCount === 1 ? (
          t("summary_page.drugs.you_have_one_saved_drug")
        ) : (
          <FormattedMessage
            id="summary_page.drugs.you_have_x_number_of_drugs"
            values={{ totalDrugCount }}
          />
        )}{" "}
        <span>{t("summary_page.drugs.description")}</span>
      </p>
      {messageToShow && (
        <AlertMessage
          className="ds-u-margin-y--3 e2e-drug-container-alert-message"
          {...{
            icon: messageToShow.icon,
            body: messageToShow.body,
            action: messageToShow.action,
            title: messageToShow.title,
            condition: messageToShow.key,
          }}
        />
      )}
      {drugsToDisplay.length > 0 && (
        <ul
          className="mct-c-summary-page-drug-cards-container__list mct-c-summary-page__cards-container"
          role="list"
          aria-labelledby="YourDrugsHeading"
        >
          {drugsToDisplay.map((drug, index) => (
            <li key={index} role="listitem">
              <DrugCard drug={drug} />
            </li>
          ))}
        </ul>
      )}
      {totalDrugCount > 0 && (
        <p className="ds-u-font-size--md" data-testid="drug-results">
          {t("summary_page.drugs.current_of_total.showing")}{" "}
          <span className="ds-u-font-weight--semibold">
            <FormattedMessage
              id="summary_page.drugs.current_of_total"
              values={{
                current: drugsToDisplay.length,
                total: totalDrugCount,
              }}
            />
          </span>{" "}
          {totalDrugCount === 1
            ? t("summary_page.drugs.current_of_total.drug")
            : t("summary_page.drugs.current_of_total.drugs")}
        </p>
      )}
      <div className="mct-c-summary-page-drug-cards-container__action-buttons">
        <Link
          to={routes.summary.managePrescriptions}
          className="ds-c-button ds-c-button--solid ds-c-button--alternate mct-hide-print e2e-drug-cards-container-action-button"
          onClick={onClickAddModifyDrugs}
        >
          {totalDrugCount > 0 ? (
            editButtonText
          ) : (
            <>
              <AddIcon className="ds-u-margin-right--1  ds-u-font-size--lg" />
              {t("summary_page.drugs.actions.add_drugs")}
            </>
          )}
        </Link>
        {showAddPDEButton && (
          <Button
            className="mct-c-summary-page-drug-cards-container__add-pde-button"
            onClick={() => {
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: t("pde_page.add_recently_filled_drugs"),
                  },
                },
              });
              onClickAddPDEs();
            }}
          >
            {t("pde_page.add_recently_filled_drugs")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DrugCardsContainer;
